import type { User } from '~~/shared/types'
import { startAuthentication } from '@simplewebauthn/browser'

/**
 * Issue d'une etape de connexion : session ouverte, ou etape suivante exigee
 * (code par email sur appareil inconnu, passkey pour un admin qui en a une).
 */
export interface LoginStep {
  otpRequired: boolean
  passkeyRequired: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)
  const authenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'superadmin')

  // Admin sans passkey : l'accueil lui en recommande une
  // Lie au compte : un autre compte ne peut pas heriter de l'etat d'avant
  const passkeyState = ref<{ userId: number, has: boolean } | null>(null)
  const hasPasskeys = computed(() =>
    passkeyState.value && passkeyState.value.userId === user.value?.id ? passkeyState.value.has : undefined)

  function setHasPasskeys(has: boolean) {
    if (user.value) passkeyState.value = { userId: user.value.id, has }
  }

  // Impersonation
  const impersonating = ref(false)
  const impersonator = ref<{ id: number, name: string, email: string } | null>(null)

  async function fetchSession() {
    loading.value = true
    try {
      const headers = import.meta.server ? useRequestHeaders(['cookie']) : undefined
      const res = await $fetch('/api/auth/session', { headers })
      const data = res.data as {
        user: User
        impersonating?: boolean
        impersonator?: { id: number, name: string, email: string } | null
        has_passkeys?: boolean
      }
      user.value = data.user
      if (data.has_passkeys !== undefined) setHasPasskeys(data.has_passkeys)
      impersonating.value = data.impersonating || false
      impersonator.value = data.impersonator || null
    }
    catch {
      user.value = null
      impersonating.value = false
      impersonator.value = null
    }
    finally {
      loading.value = false
    }
  }

  async function stopImpersonation() {
    try {
      await $fetch('/api/admin/impersonate/stop', { method: 'POST' })
      await fetchSession()
    }
    catch {
      if (import.meta.client) window.location.reload()
    }
  }

  /**
   * Connexion par mot de passe. Sur un appareil inconnu, le serveur n'ouvre
   * pas de session : il envoie un code par email et renvoie `otp_required`.
   */
  async function login(email: string, password: string): Promise<LoginStep> {
    const res = await $fetch('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    return applyStep(res.data)
  }

  /** Session ouverte, ou etape suivante demandee par le serveur. */
  function applyStep(data: { otp_required?: true, passkey_required?: true, user?: unknown }): LoginStep {
    if (data.otp_required) return { otpRequired: true, passkeyRequired: false }
    if (data.passkey_required) return { otpRequired: false, passkeyRequired: true }
    user.value = data.user as User
    return { otpRequired: false, passkeyRequired: false }
  }

  async function sendEmailCode(email: string, clientId?: string) {
    return $fetch('/api/auth/email-code', {
      method: 'POST',
      body: { email, client_id: clientId },
    })
  }

  async function verifyOtp(email: string, code: string): Promise<LoginStep> {
    const res = await $fetch('/api/auth/verify-otp', {
      method: 'POST',
      body: { email, code },
    })
    return applyStep(res.data)
  }

  /**
   * Second facteur : le navigateur demande la passkey (empreinte, cle de
   * securite, telephone), le serveur verifie la signature puis ouvre la session.
   */
  async function verifyPasskey() {
    const options = await $fetch('/api/auth/passkey/login-options', { method: 'POST' })
    const response = await startAuthentication({ optionsJSON: options.data })
    const res = await $fetch('/api/auth/passkey/login-verify', {
      method: 'POST',
      body: { response },
    })
    user.value = res.data.user as User
    setHasPasskeys(true)
  }

  async function logout() {
    try {
      await $fetch('/api/auth/logout', { method: 'POST' })
    }
    catch {
      // Ignorer les erreurs de logout
    }
    user.value = null
    passkeyState.value = null
  }

  return {
    user,
    loading,
    authenticated,
    isAdmin,
    hasPasskeys,
    setHasPasskeys,
    impersonating,
    impersonator,
    fetchSession,
    stopImpersonation,
    login,
    sendEmailCode,
    verifyOtp,
    verifyPasskey,
    logout,
  }
})
